import React from 'react';
import { Icon } from '../icon/Icon.jsx';

/* Machined register, 2026-08-01. Three things changed and each is felt rather than
   seen:

   1. **A press state.** `:active` takes the button to `--press-scale` in
      `--dur-press` on `--ease-snap`. Nothing else in an interface buys as much
      perceived responsiveness per line: without it, a control that is waiting on a
      network round trip looks like a control that did not register the click.
   2. **The filled variants carry `--edge-highlight`.** A 1px inner top highlight is
      what makes an accent fill read as a raised object rather than a coloured
      rectangle, and it is the same treatment every other raised surface gets.
   3. **The transition names its properties.** It used to be `background ... linear,
      color ... linear` — `linear` on a state change reads mechanical, and the two
      properties it did not list (border, shadow, scale) therefore snapped while the
      other two eased. `.jmt-btn` now inherits the shared transition from
      `tokens/base.css`, so every button in the product changes state the same way. */

const VARIANTS = {
  primary:   { background: 'var(--accent)', color: 'var(--bg-canvas)', border: '1px solid transparent', boxShadow: 'var(--edge-highlight-strong)' },
  secondary: { background: 'var(--bg-surface)', color: 'var(--text-primary)', border: '1px solid var(--border-default)', boxShadow: 'var(--edge-highlight)' },
  ghost:     { background: 'transparent', color: 'var(--text-secondary)', border: '1px solid transparent' },
  /* `--bg-canvas` rather than the `#FFFFFF` that was here — a raw hex in a token system,
     and a failing one. White on `--loss` is **3.36:1**, below AA, and it got worse when
     `--loss` was lightened on 2026-08-01 to clear AA as text on a dark surface. Those two
     requirements pull in opposite directions and the fill cannot satisfy both with white.

     Canvas text is 5.83:1 and makes the rule uniform: **a filled button carries
     `--bg-canvas` text**, primary and danger alike. Dark-on-red is less conventional than
     white-on-red, and it is the legible one. */
  danger:    { background: 'var(--loss)', color: 'var(--bg-canvas)', border: '1px solid transparent', boxShadow: 'var(--edge-highlight-strong)' },
  link:      { background: 'transparent', color: 'var(--accent)', border: '1px solid transparent', padding: 0, height: 'auto', textDecoration: 'underline', textUnderlineOffset: '.18em', textDecorationThickness: '1px' },
};
const HEIGHTS = { comfortable: 32, compact: 28, dense: 24 };

export function Button({
  variant = 'secondary', density = 'comfortable', icon, iconRight, disabled, loading,
  progress, children, style, ...rest
}) {
  const v = VARIANTS[variant] || VARIANTS.secondary;
  const h = HEIGHTS[density] || 32;
  const inverseRing = variant === 'primary' || variant === 'danger';

  const base = {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 'var(--space-8)',
    height: variant === 'link' ? 'auto' : h,
    padding: variant === 'link' ? 0 : (icon || iconRight ? '0 8px' : '0 12px'),
    borderRadius: variant === 'link' ? 0 : 'var(--radius-control)',
    fontFamily: 'var(--font-ui)', fontSize: '13px', fontWeight: 500, lineHeight: 1,
    letterSpacing: 'var(--body-sm-tracking)',
    cursor: disabled ? 'not-allowed' : 'pointer',
    position: 'relative', overflow: 'hidden', whiteSpace: 'nowrap',
    ...v,
    ...(disabled ? { background: 'transparent', color: 'var(--text-disabled)', border: '1px solid var(--border-subtle)', boxShadow: 'none' } : null),
    ...style,
  };

  return (
    <button
      type="button"
      disabled={disabled || loading}
      data-variant={variant}
      className={'jmt-btn' + (inverseRing ? ' jmt-btn--inverse-ring' : '')}
      style={base}
      {...rest}
    >
      {icon ? <Icon name={icon} size={16} /> : null}
      <span>{children}</span>
      {iconRight ? <Icon name={iconRight} size={16} /> : null}
      {loading && typeof progress === 'number' ? (
        <span aria-hidden="true" style={{
          position: 'absolute', left: 0, bottom: 0, height: 2, width: '100%',
          transformOrigin: 'left',
          transform: 'scaleX(' + Math.max(0, Math.min(100, progress)) / 100 + ')',
          background: 'currentColor', opacity: .5,
          /* scaleX rather than a width percentage: a determinate bar redraws at the
             1s tick and a width change is a layout pass each time. --ease-out on a
             progress bar would also lie about the rate, so this is linear */
          transition: 'transform var(--tick) linear',
        }} />
      ) : null}
      <style>{`
        .jmt-btn:focus-visible{outline:none;box-shadow:var(--focus-ring)}
        .jmt-btn--inverse-ring:focus-visible{box-shadow:var(--focus-ring-inverse)}
        .jmt-btn[data-variant=primary]:hover:not(:disabled){background:var(--accent-hover)}
        .jmt-btn[data-variant=primary]:active:not(:disabled){background:var(--accent-pressed)}
        .jmt-btn[data-variant=secondary]:hover:not(:disabled){background:var(--bg-raised);border-color:var(--border-strong)}
        .jmt-btn[data-variant=ghost]:hover:not(:disabled){background:var(--bg-raised);color:var(--text-primary)}
        .jmt-btn[data-variant=danger]:hover:not(:disabled){background:var(--loss-dim)}
        .jmt-btn[data-variant=link]:hover:not(:disabled){color:var(--accent-hover)}
        /* The link variant is text. It must not scale, or the baseline it shares with
           the sentence around it moves. */
        .jmt-btn[data-variant=link]:active:not(:disabled){scale:1}
      `}</style>
    </button>
  );
}
